import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Check, Trash2, Save, Image as ImageIcon, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  generated: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  edited: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
};

function LogoImage({ assetId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    api.get(`/admin/addon-content/asset/${assetId}/image`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); })
      .catch(() => setUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [assetId]);
  if (!url) return <div className="h-40 grid place-items-center bg-navy border border-white/10 text-gray-500 text-xs" data-testid={`logo-loading-${assetId}`}><Loader2 className="animate-spin" size={18} /></div>;
  return <img src={url} alt="Logo concept" className="h-40 w-40 object-contain bg-white/5 border border-white/10 rounded" data-testid={`logo-img-${assetId}`} />;
}

function DraftCard({ draft, onChange }) {
  const [content, setContent] = useState(draft.content || "");
  const [prompt, setPrompt] = useState(draft.image_prompt || "");
  const [busy, setBusy] = useState(null);
  const isLogo = draft.kind === "logo";
  const dirty = !isLogo && content !== (draft.content || "");
  const promptDirty = isLogo && prompt !== (draft.image_prompt || "");

  const run = async (action, fn, okMsg) => {
    setBusy(action);
    try { await fn(); if (okMsg) toast.success(okMsg); await onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-navy border border-white/10 p-4" data-testid={`draft-${draft.id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-white font-medium truncate">{draft.title}</span>
          <span className={`text-[10px] px-2 py-0.5 border capitalize shrink-0 ${STATUS_STYLE[draft.review_status] || "bg-white/5 text-gray-300 border-white/10"}`} data-testid={`draft-status-${draft.id}`}>{draft.review_status}</span>
        </div>
        <button onClick={() => run("delete", () => api.delete(`/admin/addon-content/asset/${draft.id}`), "Draft deleted")}
          disabled={!!busy} data-testid={`draft-delete-${draft.id}`} className="text-gray-500 hover:text-red-400 shrink-0" title="Delete draft">
          <Trash2 size={15} />
        </button>
      </div>

      {isLogo ? (
        <div className="space-y-3">
          <LogoImage assetId={draft.id} />
          <div>
            <label className="text-[11px] text-gray-400 uppercase tracking-wide">Image prompt (edit &amp; regenerate)</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid={`draft-prompt-${draft.id}`}
              className="w-full h-24 mt-1 bg-navy-900 border border-white/15 text-gray-200 text-xs p-2.5 focus:border-gold focus:outline-none leading-relaxed" />
            <button onClick={() => run("save", () => api.put(`/admin/addon-content/asset/${draft.id}`, { prompt }), "Prompt saved")}
              disabled={!!busy || !promptDirty} data-testid={`draft-save-prompt-${draft.id}`}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/5 disabled:opacity-40">
              {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save prompt
            </button>
          </div>
        </div>
      ) : (
        <textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid={`draft-content-${draft.id}`}
          className="w-full h-44 bg-navy-900 border border-white/15 text-gray-200 text-sm p-3 focus:border-gold focus:outline-none font-mono leading-relaxed" />
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {!isLogo && (
          <button onClick={() => run("save", () => api.put(`/admin/addon-content/asset/${draft.id}`, { content }), "Saved")}
            disabled={!!busy || !dirty} data-testid={`draft-save-${draft.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/5 disabled:opacity-40">
            {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save edits
          </button>
        )}
        <button onClick={() => run("regen", () => api.post(`/admin/addon-content/asset/${draft.id}/regenerate`), "Regenerated")}
          disabled={!!busy} data-testid={`draft-regenerate-${draft.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/5 disabled:opacity-40">
          {busy === "regen" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Regenerate
        </button>
        <button onClick={() => run("approve", () => api.post(`/admin/addon-content/asset/${draft.id}/approve`), "Approved & saved to Business DNA")}
          disabled={!!busy || draft.review_status === "approved"} data-testid={`draft-approve-${draft.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gold text-navy font-semibold hover:bg-gold-light disabled:opacity-40">
          {busy === "approve" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve → DNA
        </button>
      </div>
    </div>
  );
}

export function AddonDrafts({ projectId, addons }) {
  const [open, setOpen] = useState(false);
  const [map, setMap] = useState(null);
  const [drafts, setDrafts] = useState(null);
  const [gen, setGen] = useState(null); // `${addon}:${kind}` currently generating

  const loadDrafts = useCallback(() => {
    if (!projectId) return Promise.resolve();
    return api.get(`/admin/addon-content/${projectId}`).then((r) => setDrafts(r.data)).catch(() => setDrafts([]));
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    if (!map) api.get("/admin/addon-content/map").then((r) => setMap(r.data)).catch(() => setMap({ addons: {}, kinds: {} }));
    if (drafts === null) loadDrafts();
  }, [open, map, drafts, loadDrafts]);

  const generate = async (addonName, kind) => {
    const key = `${addonName}:${kind}`;
    setGen(key);
    try {
      await api.post(`/admin/addon-content/${projectId}/generate`, { addon_name: addonName, kind });
      toast.success("Draft generated");
      await loadDrafts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGen(null); }
  };

  if (!projectId) {
    return <div className="mt-3 text-xs text-gray-500 italic" data-testid="addon-drafts-no-project">No linked project — AI content drafting needs a project with Business DNA.</div>;
  }

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`addon-drafts-${projectId}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`addon-drafts-toggle-${projectId}`}
        className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light">
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />} <Sparkles size={14} /> AI Content Drafts
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          {/* Generate buttons per add-on / kind */}
          <div className="space-y-3">
            {(addons || []).map((a) => {
              const kinds = (map?.addons || {})[a.name] || ["copywriting"];
              return (
                <div key={a.name} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-300 w-40 shrink-0">{a.name}</span>
                  {kinds.map((k) => {
                    const kdef = (map?.kinds || {})[k] || { label: k, image: false };
                    const key = `${a.name}:${k}`;
                    return (
                      <button key={k} onClick={() => generate(a.name, k)} disabled={gen === key}
                        data-testid={`generate-${projectId}-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${k}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50">
                        {gen === key ? <Loader2 className="animate-spin" size={13} /> : (kdef.image ? <ImageIcon size={13} /> : <Sparkles size={13} />)}
                        {kdef.image && gen === key ? "Generating (~1 min)…" : `Draft ${kdef.label}`}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Existing drafts */}
          {drafts === null ? (
            <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading drafts…</div>
          ) : drafts.length === 0 ? (
            <div className="text-gray-500 text-xs italic">No AI drafts yet. Generate one above — review, edit, then Approve to save it to Business DNA.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid={`drafts-list-${projectId}`}>
              {drafts.map((d) => <DraftCard key={d.id} draft={d} onChange={loadDrafts} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
